import React from 'react';

const getOrdinal = (n: number): string => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return s[(v - 20) % 10] || s[v] || s[0];
};

const toDate = (d: any): Date | null => {
    if (!d) return null;
    if (d instanceof Date) return d;
    const raw = typeof d === 'string' ? d : (d?.value || d);
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed;
};

/**
 * Formats event date range in the requested format
 * @param startIn - Start date
 * @param endIn - End date
 * @param locale - Locale string (default: 'en')
 * @param format - Format type: 'short' (2 Dec, 2025) or 'long' (2nd December)
 * @returns Formatted date range
 */
const formatEventDateRange = (
    startIn: string | Date | null | undefined,
    endIn: string | Date | null | undefined,
    locale = 'en',
    format = 'short'
): string => {
    const start = toDate(startIn);
    const end = toDate(endIn);
    if (!start && !end) return '';

    if (format === 'long') {
        const monthName = (dt: Date) => dt.toLocaleString(locale, { month: 'short' });
        const dayPart = (dt: Date) => `${dt.getDate()}${getOrdinal(dt.getDate())}`;

        if (start && end) {
            const sameDay = start.getFullYear() === end.getFullYear() &&
                start.getMonth() === end.getMonth() &&
                start.getDate() === end.getDate();
            if (sameDay) {
                return `${dayPart(start)} ${monthName(start)}, ${start.getFullYear()}`;
            }
            const sameMonthYear = start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth();
            if (sameMonthYear) {
                return `${dayPart(start)} ${monthName(start)}, ${start.getFullYear()} - ${dayPart(end)} ${monthName(end)}, ${end.getFullYear()}`;
            }
            return `${dayPart(start)} ${monthName(start)}, ${start.getFullYear()} - ${dayPart(end)} ${monthName(end)}, ${end.getFullYear()}`;
        }
        const only = start || end;
        if (!only) return '';
        return `${dayPart(only)} ${monthName(only)}, ${only.getFullYear()}`;
    } else if (format === 'miniBasket') {
        const monthAbbr = (dt: Date) => dt.toLocaleString(locale, { month: 'short' });
        const padDay = (d: Date) => d.getDate().toString().padStart(2, '0');

        if (start && end) {
            const sameMonthYear =
                start.getFullYear() === end.getFullYear() &&
                start.getMonth() === end.getMonth();
            if (sameMonthYear) {
                return `${padDay(start)} - ${padDay(end)} ${monthAbbr(end)}, ${end.getFullYear()}`;
            }
            return `${padDay(start)} ${monthAbbr(start)} - ${padDay(end)} ${monthAbbr(end)}, ${end.getFullYear()}`;
        }
        const only = start || end;
        if (!only) return '';
        return `${padDay(only)} ${monthAbbr(only)}, ${only.getFullYear()}`;
    } else {
        const monthAbbr = (dt: Date) => dt.toLocaleString(locale, { month: 'short' });
        const padDay = (d: Date) => d.getDate().toString().padStart(2, '0');
        const single = (dt: Date) => `${padDay(dt)} ${monthAbbr(dt)}, ${dt.getFullYear()}`;

        if (start && end) {
            const sameDay = start.getFullYear() === end.getFullYear() &&
                start.getMonth() === end.getMonth() &&
                start.getDate() === end.getDate();
            if (sameDay) {
                return single(start);
            }
            return `${single(start)} - ${single(end)}`;
        }
        const only = start || end;
        if (only) return single(only);
        return '';
    }
};

export interface DateRangeProps {
    startDate?: string | Date | null;
    endDate?: string | Date | null;
    locale?: string;
    format?: 'short' | 'long' | 'miniBasket';
    className?: string;
    icon?: React.ReactNode;
    textStyle?: React.CSSProperties;
    textClassName?: string;
}

/**
 * DateRange Component
 * Displays event date range only
 */
const DateRange: React.FC<DateRangeProps> = ({
    startDate,
    endDate,
    locale = 'en',
    format = 'short',
    className = 'text-xs text-gray-600',
    icon,
    textStyle,
    textClassName
}) => {
    const dateText = formatEventDateRange(startDate, endDate, locale, format);

    if (!dateText) return null;

    if (icon) {
        return (
            <span className={className} style={{ display: 'flex', alignItems: 'center' }}>
                {icon}
                <span className={textClassName} style={textStyle}>
                    {dateText}
                </span>
            </span>
        );
    }

    return <span className={className} style={{ display: 'inline' }}>{dateText}</span>;
};

export default DateRange;
// formatEventDateRange is exported from ./dateRangeUtils to satisfy react-refresh/only-export-components
